import type { Dispatch as ToolkitDispatch } from '@reduxjs/toolkit';
import type { DynamicTypeObject, FlatStringObject } from 'commonTypes';
import dayjs from 'dayjs';
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { FieldErrorsImpl, FieldValues, UseFormTrigger } from 'react-hook-form';
import type { NavigateFunction, To } from 'react-router-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FlowTypes, SELECTED_METHOD_ID } from 'store/constants';
import type { CalculateAmountWithTaxesResponse, UIPayment, UIPayout } from 'store/formatters';
import { getAmountWithTaxes, getNonTaxableAmount, getTax } from 'store/formatters';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type {
  MerchantUserFeeResponce,
  Parameter,
  PayHubCashierContractsTaxResponse as TaxResponse,
  PayHubWidgetContractsSubmitPaymentResponse,
  PayHubWidgetContractsSubmitPayoutResponse,
} from 'store/models';
import type { CustomServerError, DepositSubmitRequestProps } from 'store/services/depositAPI';
import { depositTaxSelector, isAutoSubmittedSelector, setIsAutoSubmitted } from 'store/slices/deposit';
import { selectedPayGroupIdSelector, setNeedsInitRefetch, setUserBalance, unlockFlow } from 'store/slices/global';
import { withdrawalTaxSelector } from 'store/slices/withdrawal';

import { useAppCore } from '../providers/AppCoreProvider/AppCoreContext';
import { setFingerprint } from '../services/fingerprint';
import { getGroupAttributes, isDepositFlowType, softLocalStorageGet, stringToInt } from '../utils';
import { calculateUserFee } from '../utils/userFee';
import type { WagerFeeData } from '../utils/wagerFee';
import { calculateWagerFee } from '../utils/wagerFee';
import type { LazyGetTriggerType } from './Checkout/types';
import { setDepositInitDataToStore } from './Deposit/utils';
import { formRequestBody } from './payUtils';
import { setWithdrawalInitDataToStore } from './Withdrawal/utils';

interface UseDepositMethodHookProps {
  error?: CustomServerError;
  setSubmitGeneralError: (error: string) => void;
}

export const useFormContainerHook = (props: UseDepositMethodHookProps): void => {
  const { error, setSubmitGeneralError } = props;

  useEffect(() => {
    setFingerprint();
  }, []);

  useEffect(() => {
    if (error && error.errorCode !== 17) {
      setSubmitGeneralError(error.errorMessage || '');
    }
  }, [error]);
};

export const useGetPayGroupId = (): number => {
  const { provider = '' } = useParams();
  const payGroupId = useAppSelector(selectedPayGroupIdSelector);
  const storedPayGroupId = softLocalStorageGet(SELECTED_METHOD_ID);
  const { payGroupIdAttr } = getGroupAttributes();

  return stringToInt(provider) || payGroupId || (storedPayGroupId && stringToInt(storedPayGroupId)) || payGroupIdAttr;
};
interface TaxSelector {
  (payment: { deposit?: UIPayment; withdrawal?: UIPayout }): TaxResponse;
}
interface UseGetTaxInfo {
  tax: TaxResponse;
  calculateNonTaxableAmount: number;
  amountWithTaxes: CalculateAmountWithTaxesResponse;
}

interface UseGetTaxInfoParams {
  amount: number;
  flow: FlowTypes;
  wagerFee?: WagerFeeData | null;
  currencyPrecision?: number;
  merchantUserFee?: MerchantUserFeeResponce | null;
}

export const useGetTaxInfo = ({
  amount,
  flow,
  wagerFee,
  currencyPrecision,
  merchantUserFee,
}: UseGetTaxInfoParams): UseGetTaxInfo => {
  const FLOW_TYPE_SELECTOR_MAPPING: {
    [key: string]: TaxSelector;
  } = {
    [FlowTypes.deposit]: depositTaxSelector,
    [FlowTypes.withdrawal]: withdrawalTaxSelector,
  };
  const tax = useAppSelector(FLOW_TYPE_SELECTOR_MAPPING[flow]);
  const calculateNonTaxableAmount: number = getNonTaxableAmount(tax);
  const { userFeeAmount } = calculateUserFee(amount, merchantUserFee, currencyPrecision);
  const { wagerFeeAmount } = calculateWagerFee(amount, wagerFee, currencyPrecision);
  const baseAmount = amount - userFeeAmount - wagerFeeAmount;
  const taxValue = getTax({
    tax,
    amount: baseAmount,
  });
  const isExtraTaxInfoShown = baseAmount > calculateNonTaxableAmount && !!taxValue;
  const amountWithTaxes = getAmountWithTaxes({
    isExtraTaxInfoShown,
    tax,
    amount: baseAmount,
    calculateNonTaxableAmount,
    taxValue,
  });
  return {
    tax,
    calculateNonTaxableAmount,
    amountWithTaxes,
  };
};

export const useDetectNewErrors = (errors: Partial<FieldErrorsImpl<DynamicTypeObject>>) => {
  const [fieldErrorsCount, setFieldErrorsCount] = useState(0);
  const [hasNewErrors, sethasNewErrors] = useState(false);

  const errorsCount = Object.keys(errors).length;

  useEffect(() => {
    sethasNewErrors(fieldErrorsCount !== errorsCount);
    setFieldErrorsCount(errorsCount);
  }, [errorsCount]);

  return {
    hasNewErrors,
  };
};

export const useOnClickOutside = (ref: RefObject<HTMLElement>, handler: (event: Event) => void) => {
  useEffect(() => {
    const listener = (e: Event) => {
      const targetNode = e.target;
      if (targetNode instanceof Node && (!ref.current || ref.current.contains(targetNode))) {
        return;
      }
      handler(e);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

interface PrefillAutosubmitProps {
  amountValue: number;
  payGroupId: number;
  currencyPrecision: number;
  currency: string;
  presetCurrency?: string | null;
  autoSubmit?: boolean;
  isMethodLoaded?: boolean;
  isCheckout?: boolean;
  isDisplayForm?: boolean;
  isCachedResult?: boolean;
  requiredFieldsName: string[] | [];
  formConfig: Parameter[];
  isValid: boolean;
  values: FieldValues;
  validate: UseFormTrigger<FieldValues>;
  onComplete: () => void;
  bonusPromotionId?: string | null;
  withoutBonusPromotion?: boolean;
  submitDepositTrigger?: LazyGetTriggerType<
    DepositSubmitRequestProps,
    PayHubWidgetContractsSubmitPayoutResponse | PayHubWidgetContractsSubmitPaymentResponse
  >;
}

export const useAbortSubmitControllerRef = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleCancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      handleCancelRequest();
    };
  }, []);

  return abortControllerRef;
};

// Lock / unlock flow is used only for the multi-wallet case.
// We rely on this flag to disable tabs while async requests are in progress.
//
// This prevents the user from switching between crypto and fiat flows mid-flight,
// which could otherwise mix logic and lead to inconsistent state.
//
// unlockFlow must be called only after the entire flow is finished
// (success, failure, or early exit).

export const usePrefillAutosubmit = ({
  autoSubmit,
  currencyPrecision,
  amountValue,
  currency,
  payGroupId,
  validate,
  submitDepositTrigger,
  values,
  isValid,
  isDisplayForm,
  isCachedResult,
  onComplete,
  requiredFieldsName,
  formConfig,
  bonusPromotionId,
  withoutBonusPromotion,
}: PrefillAutosubmitProps) => {
  const { selectState } = useAppCore();
  const isSelectGroupRequestDone = selectState.isSelectGroupRequestDoneRef?.current;
  const dispatch = useAppDispatch();
  const isAutoSubmitted = useAppSelector(isAutoSubmittedSelector);
  // ✅ autosubmit within THIS mount
  const hasAutoSubmittedRef = useRef(false);
  // ✅ autosubmit currently in progress (lock flow)
  const isFlowLockedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isExistRequiredFields = requiredFieldsName.length > 0;
  const emptyRequiredFields = isExistRequiredFields
    ? requiredFieldsName.filter((fieldName: string) => !values[fieldName]?.toString().trim())
    : [];
  const preparedFields = values;

  useEffect(
    () => () => {
      dispatch(setIsAutoSubmitted(false));
    },
    [],
  );

  useEffect(() => {
    // =====================================================
    // 1️⃣ IF autosubmit is DISABLED AT ALL
    //    → this is 100% NOT an autosubmit flow
    //    → can unlock immediately
    // =====================================================
    if (!autoSubmit) {
      if (!isFlowLockedRef.current) {
        dispatch(unlockFlow());
        onComplete();
      }
      return;
    }

    // =====================================================
    // 2️⃣ Autosubmit is ENABLED
    //    conditions are NOT READY yet
    //    ❗️ NOT unlock
    // =====================================================
    const canStartAutoSubmit = isSelectGroupRequestDone && submitDepositTrigger;

    if (!canStartAutoSubmit) {
      return;
    }

    // =====================================================
    // 3️⃣ Autosubmit has already happened
    //    → do not start it a second time
    // =====================================================
    if (hasAutoSubmittedRef.current) {
      return;
    }

    // =====================================================
    // 4️⃣ Autosubmit is already in progress (async in flight)
    // =====================================================
    if (isFlowLockedRef.current) {
      return;
    }

    // =====================================================
    // 5️⃣ START AUTOSUBMIT
    // =====================================================
    hasAutoSubmittedRef.current = true;
    isFlowLockedRef.current = true;

    // ----------------------------------------
    // 5️⃣ Prepare date fields
    // ----------------------------------------
    const dateFields = formConfig.filter(({ type }) => type === 'date');

    if (dateFields.length) {
      dateFields.forEach(({ name }) => {
        preparedFields[name] = preparedFields[name] ? dayjs(preparedFields[name]).toISOString() : '';
      });
    }

    // ----------------------------------------
    // 6️⃣ Validation + submit
    // ----------------------------------------
    validate()
      .then((hasValidFields) => {
        if (!hasValidFields || emptyRequiredFields.length > 0) {
          // ❗️ if invalid — just finish the flow
          dispatch(unlockFlow());
          onComplete();
          isFlowLockedRef.current = false;
          return;
        }
        dispatch(setIsAutoSubmitted(true));
        if (isDisplayForm || isCachedResult) {
          dispatch(setNeedsInitRefetch(true));
        }
        abortControllerRef.current = new AbortController();
        const requiredFieldNames = formConfig.filter((field) => field.isRequired).map((field) => field.name);

        submitDepositTrigger({
          payGroupId,
          requestBody: formRequestBody({
            amount: `${amountValue}`,
            fields: preparedFields,
            currency,
            currencyPrecision,
            requiredFieldNames,
            bonusPromotionId,
            withoutBonusPromotion,
          }),
          signal: abortControllerRef.current.signal,
        }).finally(() => {
          // ----------------------------------------
          // 7️⃣ The only place where we unlock the flow
          // ----------------------------------------
          if (isFlowLockedRef.current) {
            dispatch(unlockFlow());
            isFlowLockedRef.current = false;
          }
        });
      })
      .catch(() => {
        dispatch(unlockFlow());
        isFlowLockedRef.current = false;
        onComplete();
      });
  }, [
    isValid,
    autoSubmit,
    isSelectGroupRequestDone,
    isAutoSubmitted,
    submitDepositTrigger,
    submitDepositTrigger,
    requiredFieldsName,
  ]);
};

export const useSetGroupDataAttributes = (currency: string, paygroupId: number) => {
  useEffect(() => {
    document.body.dataset.payGroupId = `${paygroupId}`;
    document.body.dataset.currency = currency;
  }, []);
};

interface UseReinit {
  reInitTrigger: () => void;
  currency: string;
}
export const useReinit = (props: UseReinit) => {
  const { reInitTrigger, currency } = props;

  useEffect(() => {
    if (currency) {
      return;
    }

    reInitTrigger();
  }, []);
};

interface UseSetInitDataToStore {
  initData: UIPayment & UIPayout;
  flowType: FlowTypes;
  dispatch: ToolkitDispatch;
  navigate: NavigateFunction;
  payGroupId: number;
  isFetching: boolean;
}

export const useSetInitDataToStore = (props: UseSetInitDataToStore) => {
  const { initData, flowType, dispatch, navigate, payGroupId, isFetching } = props;
  useEffect(() => {
    if (!isFetching && initData && Object.keys(initData).length && initData.isReady) {
      const setFunction = isDepositFlowType(flowType) ? setDepositInitDataToStore : setWithdrawalInitDataToStore;

      const { userBalance } = initData;

      dispatch(setUserBalance(userBalance));
      setFunction({
        initData,
        dispatch,
        navigate,
        payGroupId,
      });
    }
  }, [isFetching, initData.isReady]);
};

export const useNavigateWithSearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const nav = (to: To, options: FlatStringObject) => {
    if (typeof to === 'number') {
      navigate(to, options);
      return;
    }

    // window.location.search is the primary source (includes params added via pushState like nativeTrap).
    // React Router searchParams is the fallback when window.location.search is empty
    // (e.g. after a pushState that strips query params).
    const { search: windowSearch } = window.location;
    const routerSearch = searchParams.toString();
    const search = windowSearch || (routerSearch ? `?${routerSearch}` : '');

    navigate(
      {
        pathname: `${to}`,
        search,
      },
      options,
    );
  };

  return nav as NavigateFunction;
};
